import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  PackagePlus, Truck, DollarSign, AlertTriangle, FileText,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Field = ({ label, children, mono = false, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-sm ${mono ? 'mono' : ''}`}>{children || <span className="text-slate-400">—</span>}</div>
  </div>
);

const RestockSessionDialog = ({ open, onOpenChange, sessionId }) => {
  const { api } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    setSession(null);
    api().get(`/restock-sessions/${sessionId}`)
      .then(({ data }) => setSession(data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load session'))
      .finally(() => setLoading(false));
  }, [open, sessionId, api]);

  if (!open) return null;

  const fmt = (v, n = 2) => v == null ? '—' : Number(v).toFixed(n);
  const cur = session?.currency === 'Other' ? (session?.currency_other || 'XXX') : (session?.currency || 'USD');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="restock-session-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PackagePlus className="w-5 h-5 text-emerald-600" />
            Restock Session
            {session?.purchase_order_number && (
              <Badge variant="outline" className="ml-2 mono text-[10px]">PO {session.purchase_order_number}</Badge>
            )}
            {(session?.was_damaged || session?.was_delayed || session?.was_challenged) && (
              <Badge className="bg-amber-500 text-[10px]"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Issue</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="spinner" /></div>
        ) : !session ? (
          <p className="text-sm text-slate-500">Session not found.</p>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Header */}
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 grid grid-cols-2 gap-2">
              <Field label="Item" testid="rs-item">
                <span className="font-mono text-xs">{session.item_sku}</span> · {session.item_name}
              </Field>
              <Field label="Lot / Batch" mono testid="rs-lot">{session.batch_lot}</Field>
              <Field label="Quantity" mono testid="rs-qty">+{session.quantity} {session.uom}</Field>
              <Field label="Received" testid="rs-received-at">
                {session.received_at ? new Date(session.received_at).toLocaleString() : '—'}<br />
                <span className="text-xs text-slate-500">by {session.received_by_name || '—'}</span>
              </Field>
              <Field label="Vendor" testid="rs-vendor">{session.vendor_name}</Field>
              <Field label="Location" testid="rs-location">{session.location_name}</Field>
            </div>

            {/* Cost block */}
            {(session.unit_cost != null || (session.barrier_cost_in_currency || 0) > 0) && (
              <div className="border border-emerald-200 rounded-sm p-3 bg-emerald-50/40 space-y-2">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Costs · Currency · Landed
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Currency" mono testid="rs-currency">{cur}</Field>
                  <Field label="FX → USD" mono testid="rs-fx">{fmt(session.fx_rate, 4)}</Field>
                  <Field label="Unit Cost" mono testid="rs-unit-cost">{session.unit_cost == null ? '—' : `${fmt(session.unit_cost)} ${cur}`}</Field>
                  <Field label="Vendor Shipping" mono testid="rs-shipping">{session.shipping_cost == null ? '—' : `${fmt(session.shipping_cost)} ${cur}`}</Field>
                  <Field label="Tariffs" mono testid="rs-tariff">{fmt(session.tariff_cost)} {cur}</Field>
                  <Field label="Customs / Duty" mono testid="rs-customs">{fmt(session.customs_cost)} {cur}</Field>
                  <Field label="Brokerage" mono testid="rs-brokerage">{fmt(session.brokerage_cost)} {cur}</Field>
                  <Field label="Freight" mono testid="rs-freight">{fmt(session.freight_cost)} {cur}</Field>
                  <Field label={session.other_barrier_label || 'Other'} mono testid="rs-other">{fmt(session.other_barrier_cost)} {cur}</Field>
                </div>
                <div className="grid grid-cols-2 gap-2 border-t border-emerald-200 pt-2">
                  <Field label="Barriers Total" mono testid="rs-barriers-total">{fmt(session.barrier_cost_in_currency)} {cur}</Field>
                  <Field label="Landed Total (USD)" mono testid="rs-landed-total">${fmt(session.landed_total_usd)}</Field>
                </div>
                <div className="text-center bg-emerald-600 text-white rounded-sm p-1.5">
                  <span className="text-[10px] uppercase tracking-wider opacity-80">Landed Unit Cost</span>
                  <div className="text-base font-bold mono" data-testid="rs-landed-unit">${fmt(session.landed_unit_cost_usd, 4)} USD/unit</div>
                </div>
              </div>
            )}

            {/* Carrier block */}
            {(session.carrier || session.tracking_number || session.was_damaged || session.was_delayed || session.was_challenged) && (
              <div className="border border-slate-200 rounded-sm p-3 space-y-2">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-blue-600" /> Carrier · Condition
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Carrier" testid="rs-carrier">{session.carrier === 'Other' ? (session.carrier_other || 'Other') : session.carrier}</Field>
                  <Field label="Tracking #" mono testid="rs-tracking">{session.tracking_number}</Field>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {session.was_damaged && <Badge className="bg-red-600 text-[10px]" data-testid="rs-flag-damaged">Damaged</Badge>}
                  {session.was_delayed && <Badge className="bg-amber-600 text-[10px]" data-testid="rs-flag-delayed">Delayed</Badge>}
                  {session.was_challenged && <Badge className="bg-orange-600 text-[10px]" data-testid="rs-flag-challenged">Challenged</Badge>}
                  {!(session.was_damaged || session.was_delayed || session.was_challenged) && (
                    <Badge className="bg-emerald-600 text-[10px]">Clean delivery</Badge>
                  )}
                </div>
                {session.condition_notes && (
                  <Field label="Condition notes" testid="rs-condition-notes">
                    <div className="whitespace-pre-wrap text-xs">{session.condition_notes}</div>
                  </Field>
                )}
              </div>
            )}

            {/* Packing slip */}
            {session.packing_slip_image && (
              <div className="border border-slate-200 rounded-sm p-3 space-y-2">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Packing Slip / PO Photo
                </div>
                <a href={session.packing_slip_image} target="_blank" rel="noreferrer" data-testid="rs-packing-slip-link">
                  <img src={session.packing_slip_image} alt="Packing slip" className="max-h-80 rounded-sm border border-slate-200" />
                </a>
              </div>
            )}

            {session.notes && (
              <Field label="General notes" testid="rs-notes">
                <div className="whitespace-pre-wrap text-xs">{session.notes}</div>
              </Field>
            )}

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <Button size="sm" variant="outline" onClick={() => window.open(`/items/view/${session.item_id}`, '_blank')}
                data-testid="rs-open-item-btn">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Item
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RestockSessionDialog;
